import { Badge, Banner, Button, Empty, Input, Loader, Surface, Table, Text } from '@cloudflare/kumo'
import { ArrowsClockwise, Plus, ShieldCheck } from '@phosphor-icons/react'
import { accessGrantCreateSchema, type AccessGrantCreate, type AccessGrantView } from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { json, request } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { PageHeader } from '../components/page-header'
import { useGetToken, type GetToken } from '../lib/auth'
import { fmtTime, toMessage } from '../lib/format'

const accessGrantsApi = {
  list: (getToken: GetToken) =>
    request<{ items: AccessGrantView[] }>(getToken, '/admin/access-grants'),
  create: (getToken: GetToken, input: AccessGrantCreate) =>
    request<{ grant: AccessGrantView }>(getToken, '/admin/access-grants', json('POST', input)),
  revoke: (getToken: GetToken, id: string) =>
    request<{ grant: AccessGrantView }>(
      getToken,
      `/admin/access-grants/${encodeURIComponent(id)}/revoke`,
      { method: 'POST' },
    ),
}

/**
 * 권한 관리 (C52) — 관리자가 아닌 운영자에게 견적을 하나씩 맡기고 거둔다. 최고 관리자에게만, 설정의
 * '배정 운영자 접근'이 켜졌을 때만 사이드바에 보인다. 맡기기·거두기는 작업 기록에 남는다
 */
export function AccessGrantsPage() {
  const getToken = useGetToken()
  const [items, setItems] = useState<AccessGrantView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [quoteId, setQuoteId] = useState('')
  const [revoking, setRevoking] = useState<AccessGrantView | null>(null)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setItems((await accessGrantsApi.list(getToken)).items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  const input = accessGrantCreateSchema.safeParse({ email, scopeType: 'quote', scopeId: quoteId })

  const create = async () => {
    if (!input.success) return
    setBusy(true)
    try {
      await accessGrantsApi.create(getToken, input.data)
      setQuoteId('')
      await reload()
    } catch (err) {
      setError(toMessage(err))
      setBusy(false)
    }
  }

  const revoke = async () => {
    if (!revoking) return
    setBusy(true)
    try {
      await accessGrantsApi.revoke(getToken, revoking.id)
      setRevoking(null)
      await reload()
    } catch (err) {
      setError(toMessage(err))
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="권한 관리"
        description="관리자가 아닌 운영자에게 견적을 하나씩 맡겨요. 맡은 운영자는 그 견적만 보고 공장 견적·메모·메시지를 다룰 수 있어요. 설정·결제·환불·사용자·마감은 최고 관리자만 해요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Surface className="flex flex-col gap-4 rounded-lg p-6">
        <Text variant="secondary" size="sm">
          운영자는 먼저 앱이나 관리자 화면에서 가입하고 이메일 인증을 마쳐야 해요. 지금의 관리자
          (ADMIN_EMAILS·사용자 › 어드민)는 맡길 필요 없이 모든 화면을 써요.
        </Text>
        <div className="flex flex-wrap items-end gap-3">
          <Input
            label="운영자 이메일"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          />
          <Input
            label="견적 요청 ID"
            value={quoteId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuoteId(e.target.value)}
          />
          <Button
            icon={<Plus />}
            disabled={!input.success}
            loading={busy}
            onClick={() => void create()}
          >
            맡기기
          </Button>
        </div>
      </Surface>

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : items.length === 0 ? (
          <Empty
            icon={<ShieldCheck size={40} className="text-kumo-inactive" />}
            title="맡긴 견적이 없어요"
            description="운영자 이메일과 견적 요청 ID 로 맡겨 보세요."
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>운영자</Table.Head>
                <Table.Head>견적 요청</Table.Head>
                <Table.Head>맡김</Table.Head>
                <Table.Head>회수</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((g) => (
                <Table.Row key={g.id}>
                  <Table.Cell>{g.email}</Table.Cell>
                  <Table.Cell className="font-mono text-xs">{g.scopeId}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-xs">
                    <div>{fmtTime(g.createdAt)}</div>
                    <div className="text-kumo-subtle">{g.grantedBy}</div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-xs">
                    {g.revokedAt ? (
                      <>
                        <div>{fmtTime(g.revokedAt)}</div>
                        <div className="text-kumo-subtle">{g.revokedBy}</div>
                      </>
                    ) : (
                      <Badge variant="primary">맡는 중</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {!g.revokedAt && (
                      <Button size="sm" variant="secondary" onClick={() => setRevoking(g)}>
                        거두기
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <ConfirmDialog
        open={revoking !== null}
        title="이 견적을 거둘까요?"
        description={`${revoking?.email ?? ''} 운영자가 다음 요청부터 이 견적을 볼 수 없어요. 기록은 남아요.`}
        confirmLabel="거두기"
        busy={busy}
        onConfirm={() => void revoke()}
        onClose={() => setRevoking(null)}
      />
    </div>
  )
}
